/**
 * DL-01 — what a refused manifest request means (story §A-1 / §A-3), read off
 * the host client's error (axios: `response.status` + the body). The bodies
 * are the generated lyse/v1 8.7.0 envelopes — the discriminator is `reason`:
 *
 *   413 DownloadTooLarge   {code:'payload_too_large', reason:'download_too_large',
 *                           totalBytes, limit, message}        → "too_large"
 *   403 DownloadForbidden  {code:'forbidden', reason:'permission_denied'
 *                           | 'customer_download_not_hot', message}
 *                                                              → "forbidden" / "not_hot"
 *   404                    a DA / grant the caller cannot see (a revoked or
 *                           suspended grant too — never a 403) → "not_found"
 *   anything else                                              → "failed"
 *
 * PURE.
 */
import type { components } from "@aeriosiq/types";

type DownloadForbidden = components["schemas"]["DownloadForbidden"];
type DownloadTooLarge = components["schemas"]["DownloadTooLarge"];

export type DownloadRefusal =
  | { kind: "too_large"; totalBytes: number | null; limitBytes: number | null }
  | { kind: "not_hot"; message: string | null }
  | { kind: "forbidden"; message: string | null }
  | { kind: "not_found" }
  | { kind: "failed"; status: number | null; message: string | null };

interface ErrorShape {
  response?: { status?: number; data?: unknown };
  status?: number;
  message?: string;
}

/**
 * The body, field-wise over the two envelopes (their `code` literals differ,
 * so an intersection would be `never`), every field possibly absent (an
 * older BE, a proxy error page). The literals compared below are checked
 * against the generated unions with `satisfies`.
 */
interface Envelope {
  code?: string;
  reason?: string;
  message?: string;
  totalBytes?: DownloadTooLarge["totalBytes"];
  limit?: DownloadTooLarge["limit"];
}

const TOO_LARGE = "download_too_large" satisfies DownloadTooLarge["reason"];
const NOT_HOT =
  "customer_download_not_hot" satisfies DownloadForbidden["reason"];

function envelopeOf(data: unknown): Envelope {
  return data && typeof data === "object" ? data : {};
}

function messageOf(env: Envelope): string | null {
  return typeof env.message === "string" && env.message !== ""
    ? env.message
    : null;
}

function numberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function classifyManifestError(e: unknown): DownloadRefusal {
  const err = (e && typeof e === "object" ? e : {}) as ErrorShape;
  const status = err.response?.status ?? err.status ?? null;
  const env = envelopeOf(err.response?.data);
  if (status === 413 || env.reason === TOO_LARGE) {
    return {
      kind: "too_large",
      totalBytes: numberOrNull(env.totalBytes),
      limitBytes: numberOrNull(env.limit),
    };
  }
  if (env.reason === NOT_HOT) {
    return { kind: "not_hot", message: messageOf(env) };
  }
  if (status === 403) return { kind: "forbidden", message: messageOf(env) };
  if (status === 404) return { kind: "not_found" };
  return {
    kind: "failed",
    status,
    message:
      messageOf(env) ?? (typeof err.message === "string" ? err.message : null),
  };
}
